import _ from 'lodash'
import { match, matchPath } from 'react-router'
import { createSelector } from 'reselect'
import { userEmailOption } from 'src/components/UserDropdown/UserDropdown'
import { OrganizationRoles } from 'src/enums'
import { UserListModel } from 'src/models/user.models'
import {
  BudgetOverviewMatchParams,
  budgetRouteProps,
} from 'src/navigation/routeProps'
import { RootState } from 'src/redux/createRootReducer'
import { CreditCardsSnapshotPaginatedDataState } from 'src/redux/creditCardsSnapshotPaginatedData.reducer'
import { RecentUsersFullPaginatedDataState } from 'src/redux/recentUsersFullPaginatedData.reducer'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectCreditCardListModelsFromState,
  selectCreditCardPermissionsModelsFromState,
} from 'src/redux/selectors/data/creditCards.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectVirtualCardListModels } from 'src/redux/selectors/data/virtualCards.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { UsersFullPaginatedDataState } from 'src/redux/usersFullPaginatedData.reducer'
import { VirtualCardsSnapshotPaginatedDataState } from 'src/redux/virtualCardsSnapshotPaginatedData.reducer'

export const selectCreditCardIdFromRoute = createSelector(
  [selectCalculatedBackgroundLocation],
  (backgroundLocation) => {
    const mtch = matchPath<BudgetOverviewMatchParams>(
      backgroundLocation.pathname,
      budgetRouteProps,
    ) as match<BudgetOverviewMatchParams>
    return mtch.params.id
  },
)

export const selectCreditCardPermissions = createSelector(
  [selectCreditCardPermissionsModelsFromState, selectCreditCardIdFromRoute],
  (creditCardPermissions, creditCardId) => {
    return creditCardPermissions[creditCardId] !== undefined
      ? creditCardPermissions[creditCardId]
      : undefined
  },
)

export const selectCreditCard = createSelector(
  [selectCreditCardListModelsFromState, selectCreditCardIdFromRoute],
  (creditCards, creditCardId) => {
    return creditCards[creditCardId] !== undefined
      ? creditCards[creditCardId]
      : undefined
  },
)

export const selectCreditCardParent = createSelector(
  [selectCreditCard, selectCreditCardListModelsFromState],
  (creditCard, creditCards) => {
    return creditCard?.parentCreditCardId !== undefined
      ? creditCards[creditCard.parentCreditCardId]
      : undefined
  },
)

export const selectParentCreditCardPermissions = createSelector(
  [selectCreditCard, selectCreditCardPermissionsModelsFromState],
  (creditCard, parentCreditCardPermissions) =>
    creditCard !== undefined &&
    creditCard.parentCreditCardId !== undefined &&
    parentCreditCardPermissions[creditCard?.parentCreditCardId] !== undefined
      ? parentCreditCardPermissions[creditCard.parentCreditCardId]
      : undefined,
)

export const selectCreditCardOwner = createSelector(
  [selectCreditCard, selectUserListModelsFromState],
  (creditCard, users) =>
    creditCard !== undefined ? users[creditCard.ownerId] : undefined,
)

export const selectCreditCardUser = createSelector(
  [selectCreditCard, selectUserListModelsFromState],
  (creditCard, users) =>
    creditCard !== undefined ? users[creditCard.userId] : undefined,
)

export const selectCreditCardUsers = createSelector(
  [selectCreditCard],
  (creditCard): UserListModel[] =>
    creditCard?.budgetUsers !== undefined ? creditCard?.budgetUsers : [],
)

export const selectVirtualCardsSnapshotPaginatedData = (
  state: RootState,
): VirtualCardsSnapshotPaginatedDataState =>
  state.app.budget.budgetOverview.virtualCardsSnapshotPaginatedData
const selectUniqueVirtualCardIds = createSelector(
  [selectVirtualCardsSnapshotPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueVirtualCardListModels = createSelector(
  [selectUniqueVirtualCardIds, selectVirtualCardListModels],
  (ids, virtualCards) => ids.map((id) => virtualCards[id]),
)
export const selectFilteredVirtualCards = createSelector(
  [selectUniqueVirtualCardListModels, selectCreditCardIdFromRoute],
  (virtualCards, creditCardId) =>
    virtualCards.filter(
      (vc) => vc.isActive && vc.creditCardId === creditCardId,
    ),
)

export const selectCreditCardsSnapshotPaginatedData = (
  state: RootState,
): CreditCardsSnapshotPaginatedDataState =>
  state.app.budget.budgetOverview.creditCardsSnapshotPaginatedData
const selectUniqueCreditCardIds = createSelector(
  [selectCreditCardsSnapshotPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueCreditCardListModels = createSelector(
  [selectUniqueCreditCardIds, selectCreditCardListModelsFromState],
  (ids, creditCards) => ids.map((id) => creditCards[id]),
)
export const selectFilteredCreditCards = createSelector(
  [selectUniqueCreditCardListModels, selectCreditCardIdFromRoute],
  (creditCards, id) => creditCards.filter((cc) => cc.parentCreditCardId === id),
)

export const selectPostingAccountVerification = (
  state: RootState,
): boolean | undefined =>
  state.app.account.accountOverview.postingAccountVerification

export const selectAccountVerificationError = (
  state: RootState,
): string | undefined =>
  state.app.account.accountOverview.accountVerificationError

export const selectPostingResendAccountVerification = (
  state: RootState,
): boolean | undefined =>
  state.app.account.accountOverview.postingResendAccountVerification

export const selectAccountResendVerificationError = (
  state: RootState,
): string | undefined =>
  state.app.account.accountOverview.accountResendVerificationError

export const selectFetchingCreditCards = (
  state: RootState,
): boolean | undefined => state.app.budget.budgetOverview.fetchingCreditCards

export const selectFetchingVirtualCards = (
  state: RootState,
): boolean | undefined => state.app.budget.budgetOverview.fetchingVirtualCards
export const selectUsersFullPaginatedData = (
  state: RootState,
): UsersFullPaginatedDataState =>
  state.app.budget.budgetOverview.usersFullPaginatedData

export const selectUpdateBudgetError = (state: RootState): string | undefined =>
  state.app.budget.budgetOverview.updateBudgetError

export const selectRemovingBudgetUser = (state: RootState): boolean =>
  state.app.budget.budgetOverview.removingBudgetUser

export const selectRemovingBudgetUserTopLevelError = (
  state: RootState,
): string | undefined =>
  state.app.budget.budgetOverview.removingBudgetUserTopLevelError

const selectUniqueUserIds = createSelector(
  [selectUsersFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)

const selectUniqueUserEmailOptions = createSelector(
  [selectUniqueUserIds, selectUserListModelsFromState],
  (ids, users) => ids.map((id) => userEmailOption(users[id])),
)

export const selectRecentUsersFullPaginatedData = (
  state: RootState,
): RecentUsersFullPaginatedDataState | undefined =>
  state.app.budget.budgetOverview.recentUsersFullPaginatedData

const selectUniqueRecentUserIds = createSelector(
  [selectRecentUsersFullPaginatedData],
  (data) => data?.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueRecentUserEmailOptions = createSelector(
  [selectUniqueRecentUserIds, selectUserListModelsFromState],
  (ids, users) => ids?.map((id) => userEmailOption(users[id])),
)

export const selectFilteredRecentUserEmailOptions = createSelector(
  [selectUniqueRecentUserEmailOptions, selectUserListModelFromState],
  (options, user) => {
    const ownUser = options?.find(
      (option) => option.details?.user?.id === user?.id,
    )
    const filteredOptions = options?.filter(
      (option) =>
        option.details?.user?.id !== user?.id &&
        _.get(option.details, 'user.id', undefined) !== user?.id &&
        (user?.hasOrgAdminPermissions ||
          user?.organizationId === option.details?.user?.organizationId),
    )
    if (ownUser) {
      filteredOptions?.unshift(ownUser)
    }
    return filteredOptions?.slice(0, 5)
  },
)

export const selectFilteredUserEmailOptions = createSelector(
  [
    selectUniqueUserEmailOptions,
    selectUserListModelFromState,
    selectFilteredRecentUserEmailOptions,
  ],
  (options, user, suggested) => {
    return options.filter(
      (option) =>
        (user?.hasOrgAdminPermissions ||
          user?.organizationId === option.details?.user?.organizationId) &&
        suggested
          ?.map((suggestion) => suggestion.value)
          .indexOf(option.value) === -1,
    )
  },
)

export const selectFilteredUserEmailOptionsWithoutBudgetUsers = createSelector(
  [
    selectFilteredUserEmailOptions,
    selectUserListModelFromState,
    selectCreditCardUsers,
  ],
  (filteredEmails, user, budgetUsers) =>
    filteredEmails.filter(
      (person) =>
        (user?.hasOrgAdminPermissions ||
          user?.organizationId === person.details?.user?.organizationId) &&
        person.details?.user.verified &&
        budgetUsers
          ?.map((budgetUser) => budgetUser.email)
          .indexOf(person.value) === -1,
    ),
)

export const selectFilteredUserEmailOptionsWithoutBudgetUsersOrGuests =
  createSelector(
    [
      selectFilteredUserEmailOptions,
      selectUserListModelFromState,
      selectCreditCardUsers,
    ],
    (filteredEmails, user, budgetUsers) =>
      filteredEmails.filter(
        (person) =>
          (user?.hasOrgAdminPermissions ||
            user?.organizationId === person.details?.user?.organizationId) &&
          person.details?.user?.organizationRole !== OrganizationRoles.guest &&
          person.details?.user.verified &&
          budgetUsers
            ?.map((budgetUser) => budgetUser.email)
            .indexOf(person.value) === -1,
      ),
  )

export const selectFilteredRecentUserEmailOptionsWithoutBudgetUsers =
  createSelector(
    [
      selectFilteredRecentUserEmailOptions,
      selectUserListModelFromState,
      selectCreditCardUsers,
    ],
    (filteredRecentEmails, user, budgetUsers) =>
      filteredRecentEmails?.filter(
        (person) =>
          (user?.hasOrgAdminPermissions ||
            user?.organizationId === person.details?.user?.organizationId) &&
          person.details?.user.verified &&
          budgetUsers
            ?.map((budgetUser) => budgetUser.email)
            .indexOf(person.value) === -1,
      ),
  )

export const selectFilteredRecentUserEmailOptionsWithoutBudgetUsersOrGuests =
  createSelector(
    [
      selectFilteredRecentUserEmailOptions,
      selectUserListModelFromState,
      selectCreditCardUsers,
    ],
    (filteredRecentEmails, user, budgetUsers) =>
      filteredRecentEmails?.filter(
        (person) =>
          (user?.hasOrgAdminPermissions ||
            user?.organizationId === person.details?.user?.organizationId) &&
          person.details?.user?.organizationRole !== OrganizationRoles.guest &&
          person.details?.user.verified &&
          budgetUsers
            ?.map((budgetUser) => budgetUser.email)
            .indexOf(person.value) === -1,
      ),
  )
